import type {
  ExchangeCodeRequest,
  ExchangeCodeResponse,
  SupersetIntegrationDto,
} from "@superblocksteam/shared";

import { HttpMethod, callServer } from "../../store/utils/client";

/**
 * Fetches the integration metadata that the OAuth callback page needs to drive
 * the rest of the OAuth flow.
 *
 * Why this is not a plain `callServer` call:
 *
 * The OAuth callback page is hosted on `app.superblocks.com` (or a deployed-app
 * host such as `<uuid>.edit.deployed-apps.superblocks.com` in some flows).
 * Shell-driven embed popups authenticate with an explicit Superblocks runtime
 * token. Older embed paths can still fall back to the host-only `embed-token`
 * cookie on whichever host issued it.
 *
 * The default `callServer` base URL is `SUPERBLOCKS_UI_API_ENDPOINT_BASE_URL`,
 * which in production points at `https://api.superblocks.com/api/`. That is a
 * different host from where the cookie lives, so the browser does not send the
 * cookie, the server treats the request as a `VISITOR`, and RBAC returns a 403
 * (`You do not have permission to perform this action.`).
 *
 * Forcing the base URL to the same-origin `/api/` keeps the explicit bearer
 * request and the cookie fallback on the current host. See APPS-4086 for the
 * full investigation.
 */
export async function fetchOAuthCallbackIntegration(
  integrationId: string,
  profileKey: string | undefined,
  accessToken?: string,
): Promise<SupersetIntegrationDto> {
  return callServer<SupersetIntegrationDto>({
    baseUrlOverride: "/api/",
    headers: buildAuthorizationHeaders(accessToken),
    method: HttpMethod.Get,
    query: profileKey ? { profile: profileKey } : {},
    url: `v1/integrations/${integrationId}`,
  });
}

/**
 * POSTs the OAuth `accessCode` to the server to complete the code-exchange
 * step of the OAuth callback flow.
 *
 * Same same-origin override as `fetchOAuthCallbackIntegration` and for the
 * same reason: shell-driven embed popups authenticate with an explicit runtime
 * token, while older embed paths can still fall back to the host-only
 * `embed-token` cookie.
 *
 * Forcing the base URL to the same-origin `/api/` keeps both paths on the
 * current host. See APPS-4086.
 */
export async function exchangeOAuthCallbackCode(
  requestBody: ExchangeCodeRequest,
  accessToken?: string,
): Promise<ExchangeCodeResponse> {
  return callServer<ExchangeCodeResponse>({
    baseUrlOverride: "/api/",
    body: requestBody,
    headers: buildAuthorizationHeaders(accessToken),
    method: HttpMethod.Post,
    url: "v1/oauth2/exchange-code",
  });
}

function buildAuthorizationHeaders(
  accessToken: string | undefined,
): Record<string, string> | undefined {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;
}

/**
 * Request unpartitioned cookie access for the OAuth callback popup.
 *
 * Why this is needed:
 *
 * The OAuth callback page is opened as a popup from the embed shell iframe,
 * which is itself a third party under the customer's host page. Modern
 * browsers (Chrome 3PCD, Safari ITP, Firefox ETP) propagate the iframe's
 * partitioned storage into the popup it opens, even though the popup is a
 * top-level browsing context. As a result, the `embed-token` cookie used by
 * the legacy callback fallback can be invisible to the popup and the calls
 * return 403 / VISITOR.
 *
 * `document.requestStorageAccess({ cookies: true })` asks the browser to
 * un-partition the cookie jar for this popup. The granular `{ cookies:
 * true }` form is load-bearing: it leaves localStorage / IndexedDB
 * partitioned, which is exactly what the popup<->shell handoff via
 * `oauth-redirect-finished` (drive-oauth-popup.ts) needs in order to land
 * in the same partition the shell iframe is reading.
 *
 * Browser support:
 *
 * - Chrome 119+, Firefox 65+, Safari 11.1+ all expose
 *   `document.requestStorageAccess`. The `{ cookies: true }` argument is
 *   recognized by Chrome / Firefox; older Safari that predates the granular
 *   form silently ignores the argument and behaves as the parameterless
 *   form, which is also acceptable here (cookies are the only state the
 *   popup actually needs).
 * - Browsers that don't implement the API at all (e.g., very old or
 *   pre-3PCD environments where the popup is not partitioned) return
 *   `granted: true` so callers don't block the flow.
 *
 * Caller contract:
 *
 * - `granted: true` -> the popup has the same cookie view as a top-level
 *   `app.superblocks.com` page. Embed-token-cookie API calls work.
 * - `granted: false` -> the browser denied the request (incognito with no
 *   prior first-party visit, user denied a Safari prompt, etc.). The
 *   embed code-exchange path will not work; callers should surface a
 *   deterministic error instead of letting the API call 403 mysteriously.
 */
export async function requestUnpartitionedCookies(): Promise<{
  granted: boolean;
}> {
  if (typeof document === "undefined") {
    return { granted: true };
  }
  // Cast through `unknown` because TS lib.dom in the repo's TypeScript
  // version still types `requestStorageAccess()` as parameterless on
  // `Document`. Browsers that ignore the argument fall through to the
  // parameterless behavior, which is the same outcome we'd get with a
  // separate fallback call.
  const doc = document as unknown as {
    requestStorageAccess?: (opts?: { cookies?: boolean }) => Promise<void>;
  };
  if (typeof doc.requestStorageAccess !== "function") {
    return { granted: true };
  }
  try {
    await doc.requestStorageAccess({ cookies: true });
    return { granted: true };
  } catch {
    return { granted: false };
  }
}
